import get from 'lodash.get';

export interface RegistrationUrl {
  url: string;
  attribution: boolean;
}

type EntityLike = Record<string, unknown>;

/**
 * Resolves the registration/ticket URL for a campaign and whether attribution
 * tracking applies.
 *
 * - If the campaign is linked to an event (`eventId` is set), the URL is built
 *   from the parent event's ticketButtonSettings (external registration).
 *   Attribution is false in this case.
 * - If the campaign has its own ticketing enabled (`ticketing.enabled`), the
 *   URL is `{urlFull}/tickets` and attribution is true.
 * - Otherwise returns null (no registration link).
 */
export function getRegistrationUrl(entity: EntityLike): RegistrationUrl | null {
  const eventId = get(entity, 'eventId');

  if (eventId) {
    const url = buildEventTicketUrl(entity);
    return url ? { url, attribution: false } : null;
  }

  const ticketingEnabled = get(entity, 'ticketing.enabled');
  if (ticketingEnabled) {
    const urlFull = get(entity, 'urlFull', '') as string;
    return urlFull ? { url: `${urlFull}/tickets`, attribution: true } : null;
  }

  return null;
}

function buildEventTicketUrl(entity: EntityLike): string {
  const template = get(entity, 'event.template.value');
  if (!template) return '';

  const purchaseTicketUrl =
    (get(template, 'ticketingSetup.purchaseTicketUrl') as string | undefined) ||
    (get(template, 'purchaseTicketUrl') as string | undefined) ||
    '';

  if (!purchaseTicketUrl) return '';

  const settings = get(template, 'customSetup.ticketButtonSettings');
  if (!settings) return purchaseTicketUrl;

  const parameterType = get(settings, 'parameterType');
  const useOrgUrl = get(settings, 'useOrgUrl')
    ? (get(entity, 'urlPath') as string)
    : '';
  const useUrl = get(settings, 'useUrl') ? (get(entity, 'url') as string) : '';
  const campaignId = get(entity, 'id') as string | number | undefined;
  const value = useUrl || useOrgUrl || campaignId;

  if (!value) return purchaseTicketUrl;

  if (parameterType === 'route') {
    return appendToUrl(purchaseTicketUrl, String(value));
  }

  const parameterName = (get(settings, 'parameterName') as string) || 'charity';
  return setQueryParameter(purchaseTicketUrl, parameterName, String(value));
}

function appendToUrl(url: string, route: string): string {
  if (!url) return '';
  const splitIdx = url.search(/[?#]/);
  const path = splitIdx === -1 ? url : url.substring(0, splitIdx);
  const suffix = splitIdx === -1 ? '' : url.substring(splitIdx);
  const joined = path.endsWith('/') ? path + route : `${path}/${route}`;
  return joined + suffix;
}

function setQueryParameter(url: string, key: string, value: string): string {
  if (!url) return '';

  try {
    const parsed = new URL(url);
    parsed.searchParams.set(key, value);
    return parsed.toString();
  } catch {
    const hashIndex = url.indexOf('#');
    const hash = hashIndex !== -1 ? url.substring(hashIndex) : '';
    const base = hashIndex !== -1 ? url.substring(0, hashIndex) : url;

    const qIndex = base.indexOf('?');
    const path = qIndex !== -1 ? base.substring(0, qIndex) : base;
    const existingParams = qIndex !== -1 ? base.substring(qIndex + 1) : '';

    const params = new URLSearchParams(existingParams);
    params.set(key, value);

    return `${path}?${params.toString()}${hash}`;
  }
}
